import {useCallback, useEffect, useRef, useState} from "react";

import {useIsMounted} from "@khanacademy/wonder-blocks-core";
import {
    ClearPolicy,
    SchedulePolicy,
    useTimeout,
} from "@khanacademy/wonder-blocks-timing";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {getLastInteractionId, speechToText} from "@khan/guide-core";
import {useGuideState} from "@khan/guide-state";
import {Errors, KAError} from "@khan/logging";

import type {SpeechToTextResult} from "./chat-input/use-speech-to-text";

const MAX_RECORDING_DURATION_MS = 30 * 1000; // 30 seconds

/**
 * A hook that provides speech-to-text functionality using frontend audio
 * recording and backend transcription. Audio is recorded using the browser
 * MediaRecorder interface provided by the MediaStream Recording API, and is
 * then sent in full to a backend audio transcription endpoint. This does not
 * support live transcription and serves as a fallback for browsers that don't
 * support the Web Speech API.
 *
 * Because this hook does not use live transcription, the interimTranscription
 * is always an empty string, and the transcribing state is always true if the
 * recording is active.
 */
export const useAudioSpeechToText = (): SpeechToTextResult => {
    const [isOn, setIsOn] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [transcript, setTranscript] = useState("");

    const mediaRecorderRef = useRef<Nullable<MediaRecorder>>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const isMounted = useIsMounted();

    const conversation = useGuideState("conversation");
    const navigation = useGuideState("navigation");
    const threadId = useGuideState("threadId");

    const maybeStopRecording = useCallback(() => {
        if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state === "recording"
        ) {
            mediaRecorderRef.current.stop();
        }
    }, []);

    const recordingTimer = useTimeout(
        () => {
            maybeStopRecording();

            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideAudioSpeechToTextTimeout",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigation.id,
                aiGuideInteractionId: getLastInteractionId(conversation),
            });
        },
        MAX_RECORDING_DURATION_MS,
        {
            // Don't run timeout action when recording is manually stopped
            clearPolicy: ClearPolicy.Cancel,
            // Only start timer when recording starts
            schedulePolicy: SchedulePolicy.OnDemand,
        },
    );

    const startRecording = useCallback(async () => {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
        });

        setIsOn(true);

        const mimeType = getSupportedMimeType();
        const mediaRecorder = new MediaRecorder(stream, {
            mimeType,
        });

        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
            audioChunksRef.current.push(event.data);
        };

        mediaRecorder.onstop = async () => {
            // Clean up the media stream
            stream.getTracks().forEach((track) => track.stop());
            setIsTranscribing(false);

            // Only transcribe if component is still mounted
            if (!isMounted()) {
                return;
            }

            const audioBlob = new Blob(audioChunksRef.current, {
                type: mimeType,
            });

            try {
                const transcript = await speechToText(audioBlob);
                setTranscript(transcript);
            } finally {
                setIsOn(false);
            }
        };

        setTranscript("");
        setIsTranscribing(true);
        mediaRecorder.start();

        // Set up timer to automatically stop recording after duration limit
        recordingTimer.set();
    }, [isMounted, recordingTimer]);

    const stopRecording = useCallback(() => {
        recordingTimer.clear();
        maybeStopRecording();
    }, [recordingTimer, maybeStopRecording]);

    const toggleSpeechRecognition = useCallback(() => {
        if (isOn) {
            stopRecording();
        } else {
            startRecording();
        }
    }, [isOn, startRecording, stopRecording]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            recordingTimer.clear();

            if (
                mediaRecorderRef.current &&
                mediaRecorderRef.current.state === "recording"
            ) {
                mediaRecorderRef.current.stop();
            }
        };
    }, [recordingTimer]);

    return {
        on: isOn,
        transcribing: isTranscribing,
        lastTranscription: transcript,
        interimTranscription: "", // Backend speech to text does not support interim transcription
        toggleSpeechRecognition,
        isSupportedBrowser: "MediaRecorder" in window,
    };
};

const getSupportedMimeType = () => {
    const formats = [
        // WebM is preferred and is widely supported on modern browsers
        "audio/webm;codecs=opus", // Preferred: high quality, good compression
        "audio/webm", // Browser chooses codec

        // MP4 is our fallback for older versions of Safari (<v18.4)
        "audio/mp4",
    ];

    for (const format of formats) {
        if (MediaRecorder.isTypeSupported(format)) {
            return format;
        }
    }

    throw new KAError("No supported audio format found", Errors.Internal);
};
